/**
 * Messages Hooks
 * 
 * React Query hooks for club messaging functionality.
 * Follows ADR-0001 pattern: Component → Hooks → Domain API → HTTP Wrapper
 * 
 * Production-safe optimistic updates with immutable cache operations.
 * Issue #607: 6-8 second delay → <100ms instant UX
 */

import { useInfiniteQuery, useMutation, useQueryClient, type InfiniteData } from '@tanstack/react-query';
import { messagesApi, type Message, type SendMessagePayload } from '../lib/api/messages';
import { useAuth } from './useAuth';

interface MessagesPage {
  messages: Message[];
  nextCursor: number | undefined;
}

interface SendMessageContext {
  previousData?: InfiniteData<MessagesPage>;
  tempId: string;
}

/**
 * Hook to fetch club messages with paginated loading
 * Polls every 30 seconds for new messages (reduced from 5s per #607)
 */
export function useClubMessages(clubId: string, limit: number = 50) {
  return useInfiniteQuery<MessagesPage, Error, InfiniteData<MessagesPage>, [string, { clubId: string }], number | undefined>({
    queryKey: ['messages', { clubId }],
    queryFn: async ({ pageParam }) => {
      const messages = await messagesApi.getClubMessages(
        clubId,
        limit,
        pageParam
      );
      
      // Messages come newest-first from API, reverse for display
      const reversed = messages.reverse();
      return {
        messages: reversed,
        nextCursor: messages.length === limit ? messages[0]?.created_at : undefined,
      };
    },
    getNextPageParam: (lastPage) => lastPage.nextCursor,
    initialPageParam: undefined,
    enabled: !!clubId,
    refetchInterval: 30000, // Poll every 30 seconds (reduced from 5s - Issue #607)
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    staleTime: 0,
  });
}

/**
 * Hook to send a message to a club
 * TRUE optimistic updates with safe rollback (Issue #607)
 * Messages appear instantly, then get real ID from server
 */
export function useSendMessage(clubId: string) {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: (payload: SendMessagePayload) => 
      messagesApi.sendMessage(clubId, payload),
    
    // OPTIMISTIC UPDATE - happens BEFORE server request (instant UI)
    onMutate: async (payload): Promise<SendMessageContext> => {
      // Cancel outgoing refetches to avoid race conditions
      await queryClient.cancelQueries({ queryKey: ['messages', { clubId }] });

      // Snapshot previous value for rollback
      const previousData = queryClient.getQueryData<InfiniteData<MessagesPage>>([
        'messages',
        { clubId },
      ]);

      // Create UNIQUE temp ID (critical: prevents duplicate message bug)
      const tempId = `temp-${Date.now()}-${
        globalThis.crypto?.randomUUID?.() ?? Math.random().toString(36).slice(2)
      }`;

      // Create optimistic message (will be replaced by real one)
      const optimisticMessage: Message = {
        id: tempId,
        club_id: clubId,
        user_id: user?.id || '',
        user_name: user ? `${user.first_name} ${user.last_name}` : 'You',
        user_level: 'free', // Server will correct this
        subscription_level: null,
        profile_image_url: user?.profile_image_url || null,
        club_role: 'member',
        body: payload.body,
        image_url: payload.image_urls?.[0] || undefined,
        image_urls: payload.image_urls || null,
        created_at: Math.floor(Date.now() / 1000),
      };

      // Optimistically update cache (FULLY IMMUTABLE)
      if (previousData) {
        const nextPages = previousData.pages.map((page, idx) => {
          const isLastPage = idx === previousData.pages.length - 1;
          if (!isLastPage) return page;

          // Deep clone: create new page object with new messages array
          return {
            ...page,
            messages: [...page.messages, optimisticMessage],
          };
        });

        queryClient.setQueryData<InfiniteData<MessagesPage>>(
          ['messages', { clubId }],
          {
            ...previousData,
            pages: nextPages,
          }
        );
      }

      return { previousData, tempId };
    },

    // Replace ONLY the matching temp message with server response
    onSuccess: (response, _variables, context) => {
      if (!context?.tempId) {
        console.warn('[useMessages] onSuccess called without tempId in context');
        return;
      }

      const current = queryClient.getQueryData<InfiniteData<MessagesPage>>([
        'messages',
        { clubId },
      ]);

      if (!current) return;

      // IMMUTABLE UPDATE: Only swap the message with matching tempId
      const nextPages = current.pages.map((page) => ({
        ...page,
        messages: page.messages.map((msg) =>
          msg.id === context.tempId ? response.message : msg
        ),
      }));

      queryClient.setQueryData<InfiniteData<MessagesPage>>(
        ['messages', { clubId }],
        {
          ...current,
          pages: nextPages,
        }
      );
    },

    // Rollback on error (restores snapshot)
    onError: (_error, _variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(
          ['messages', { clubId }],
          context.previousData
        );
      }
      // TODO: Add error toast notification here
    },

    // Revalidate from server after success (ensures consistency)
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['messages', { clubId }] });
    },
  });
}
